import React, { useMemo, useEffect, useCallback } from 'react';
import useAttention from 'context/AttentionContext';
import styles from 'assets/css/CertificationExam.module.css';
import buttonStyles from 'assets/css/Button.module.css';
import courseStyles from 'assets/css/CourseComponent.module.css';
import CardLink, { CardLinkIconWrap } from 'Components/Card/CardLink';
import { useOverlayToggle } from '@ace/react-utilities';
import overlayStyles from 'assets/css/ToolTipOverlay.module.css';
import { IconACECertified, IconAngleRight, IconClock, IconCheckmarkCircle, IconCautionTriangle, IconSlashCircle, IconDashCircle } from '@ace/react-icons';
import certBadgeImg from 'assets/images/cert-badge-icon.svg';

function CertificationExamCard (props) {
    //render
    return useMemo(() => (
        <div>
            <div className={styles.examBodyWrap}>
                <ExamBody examData={props.data} />
            </div>
            <ExamHistoryContainer examHistory={props.data.ExamHistory} />
        </div>
    ), [props.data]);
}

function ExamBody ({ examData }) {
    switch (examData.CardDisplayType) {
        case CardViewType.NO_VOUCHER:
            return <NoVoucherDisplay certificationType={examData.CertificationType} purchaseBtn={examData.NoVoucherPurchaseButton} />;
        case CardViewType.VOUCHER:
            return <VoucherDisplay voucher={examData.Voucher} />;
        case CardViewType.EXAM_WITH_SCHEDULED_EVENT:
            return <ExamWithScheduledEventDisplay exam={examData.ScheduledEvent} />;
        case CardViewType.EXAM_RESULTS:
            return <ExamResultsDisplay examResult={examData.ExamResults} />;
        default:
            return false;
    }
}

const NoVoucherDisplay = ({ certificationType, purchaseBtn }) => useMemo(
    () => (
        <div className={styles.examPurchase}>
            <h2 className={styles.examPurchaseTitle}>
                ACE Certified {certificationType} Exam
            </h2>

            <div className={styles.examPurchaseRow}>
                <img src={certBadgeImg} alt="Certification Exam Badge" />
                <p>
                    Level up and get the premium NCCA-accredited certification with this proctored exam. Click below to purchase.
                </p>
            </div>

            <CardLink
                linkText={purchaseBtn.CardLinkText}
                linkIcon={<IconAngleRight />}
                linkUrl={purchaseBtn.CardLinkUri}
                target={"_blank"}
                className={`${buttonStyles.purchase}`}
            />
        </div>
    ), [purchaseBtn.CardLinkText, purchaseBtn.CardLinkUri]
);

function VoucherDisplay ({ voucher }) {
    const {
        setAttentionWarning,
        clearAttentionState
    } = useAttention();

    useEffect(() => {
        if (voucher.Expired) {
            setAttentionWarning();
        }
        else {
            clearAttentionState();
        }
    }, [voucher.Expired, setAttentionWarning, clearAttentionState]);

    const VoucherCounter = useCallback(({ expired, daysTilExpiration }) => {
        let label = expired ? "Expired" : "Days";
        let counterClass = `voucher${expired ? "Expired" : "Days"}`;

        return (
            <div className={styles.date}>
                <span className={styles[counterClass]}>
                    {expired ? <IconCautionTriangle fill="#02263C" /> : daysTilExpiration}
                </span>
                {label}
            </div>
        );
    }, []);

    return useMemo(() => (
        <div className={`${styles.container} ${styles.voucherExpirationWrap}`}>
            <VoucherCounter expired={voucher.Expired} daysTilExpiration={voucher.DaysTilExpiration} />
            <div className={styles.contentWrap}>
                <p className={styles.subTitle}>Your exam voucher {voucher.Expired ? "expired" : "expires"}:</p>
                <h4 className={styles.title}>{voucher.ExpirationDateDisplay}</h4>
            </div>
        </div>
    ), [voucher.Expired, voucher.ExpirationDateDisplay, voucher.DaysTilExpiration]);
}

function ExamWithScheduledEventDisplay ({ exam }) {

    const ExamLocation = useCallback(({ exam }) => {
        if (!exam.SiteHasAddress) {
            return false;
        }

        return (
            <>
                <div className={styles.examAddress}>
                    {exam.Address1}<br />
                    {exam.City}, {exam.State} {exam.PostalCode}, {exam.Country}
                </div>
                <div className={styles.examLinks}>
                    {
                        exam.LocationLinks.map(link => {
                            return (
                                <CardLink
                                    linkText={link.CardLinkText}
                                    linkUrl={link.CardLinkUri}
                                    target={"_blank"}
                                    linkIcon={<IconAngleRight />}
                                    className={buttonStyles.floating} />
                            )
                        })
                    }
                </div>
            </>
        );
    }, []);

    return useMemo(() => (
        <div className={styles.container}>
            <div className={styles.date}>
                {exam.ExamMonth}
                <span>{exam.ExamDay}</span>
            </div>
            <div className={styles.contentWrap}>
                <ExamStatus status={exam.Status} />
                <div className={`${styles.title} ${styles.examDate}`}>{exam.ExamDateDisplay}</div>
                <div className={styles.examTime}>{exam.ExamTimeDisplay}</div>
                <div className={styles.venueName}>{exam.VenueName}</div>
                <ExamLocation exam={exam} />
                {exam.CountdownChecklist.ShowCountdown ? <ExamCountdownChecklist countdownChecklist={exam.CountdownChecklist} /> : false}
            </div>
        </div>
    ), [exam]);
}

const ExamCountdownChecklist = ({ countdownChecklist }) => {
    const { showOverlay, toggleOverlay, overlayRef } = useOverlayToggle();

    const handleOverlayClick = useCallback(() => {
        toggleOverlay();
    }, [toggleOverlay]);

    const OverlayContent = useMemo(
        () => {
            // eslint-disable-next-line no-script-url
            const hasPrintTicketLink = countdownChecklist.PrintTicketHref !== 'javascript:';
            const isLRP = countdownChecklist.isLRP;
            const PrintTicketLink = () => {
                if (hasPrintTicketLink) {
                    return (
                        <a href={countdownChecklist.PrintTicketHref} target={"_blank"} rel={"noopener noreferrer"}>{countdownChecklist.PrintTicketText}</a>
                    );
                }
                return <>{countdownChecklist.PrintTicketText}</>;
            }

            const CountdownChecklist = () => {
                if (isLRP) {
                    return (
                        <>
                            <li>Make Sure Name on Admission Ticket Matches Your Photo ID. Not a Match? Call us at <a href={"tel:888-825-3636,783"}>888-825-3636 EXT. 783.</a></li>
                            <li>Log into <a href="https://www.scantronassessment.com/registration">testing agency portal</a> no sooner than 15 minutes prior to exam start time  </li>
                            <li>Take Exam</li>
                        </>
                    );
                }

                return <>
                    <li>Make Sure Name on Admission Ticket Matches Your Photo ID. Not a Match? Call us at <a href={"tel:888-825-3636,783"}>888-825-3636 EXT. 783.</a></li>
                    <li>Take Exam</li>
                </>
            }

            return (
                <div ref={overlayRef} className={`${overlayStyles.overlayWrap} ${overlayStyles.showOverlay}`}>
                    <div>
                        <div className={overlayStyles.overlayClose} onClick={() => handleOverlayClick()}>x</div>
                        <div>
                            <h4>Countdown to Exam Steps</h4>
                            <ol>
                                <li><PrintTicketLink /></li>
                                <CountdownChecklist />
                            </ol>
                        </div>
                    </div>
                </div>
            )
        }
        , [handleOverlayClick, overlayRef, countdownChecklist.PrintTicketHref, countdownChecklist.PrintTicketText, countdownChecklist.isLRP]
    );

    const OverlayLink = useMemo(
        () => (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a className={buttonStyles.stroke} onClick={() => handleOverlayClick()}>
                {"Exam Countdown Checklist"}
                <CardLinkIconWrap><IconAngleRight /></CardLinkIconWrap>
            </a>
        ),
        [handleOverlayClick]
    );

    return (
        <div className={styles.examCountdown}>
            {OverlayLink}
            <div>
                {showOverlay ? (OverlayContent) : false}
            </div>
        </div>
    )
}

function ExamResultsDisplay ({ examResult }) {
    const ResultContent = useCallback(({ actionLink, title, subTitle }) => (
        <div classname={"exam-result-content-wrap"}>
            <h4 className={styles.title}>{title}</h4>
            <div className={styles.subTitle}>{subTitle}</div>
            <CardLink
                linkText={actionLink.CardLinkText}
                linkUrl={actionLink.CardLinkUri}
                linkIcon={<IconAngleRight />}
                target={"_blank"}
                className={buttonStyles.stroke} />
        </div>
    ), []
    );

    return useMemo(
        () => {
            if (examResult.Passed) {
                return (
                    <div className={styles.container}>
                        <IconACECertified className={styles.aceCertifiedIcon} />
                        <ResultContent actionLink={examResult.ActionLink} title={examResult.Title} subTitle={examResult.SubTitle} />
                    </div>
                );
            }

            return (
                <ResultContent actionLink={examResult.ActionLink} title={examResult.Title} subTitle={examResult.SubTitle} />
            );
        }, [examResult.ActionLink, examResult.Title, examResult.SubTitle, examResult.Passed]
    );
}

function ExamStatus ({ status }) {
    const {
        setAttentionWarning,
        clearAttentionState
    } = useAttention();

    useEffect(() => {
        switch (status.ExamStatus) {
            case ExamStatusDisplay.PENDINGRESELECT:
            case ExamStatusDisplay.NOTAPPROVED:
            case ExamStatusDisplay.ONHOLD:
            case ExamStatusDisplay.PENDINGPROVIDERSCHEDULING:
                setAttentionWarning();
                break;
            default:
                clearAttentionState();
                break;
        }
    }, [clearAttentionState, setAttentionWarning, status.ExamStatus]);

    const IconToUse = useCallback(({ examStatus }) => {
        switch (examStatus) {
            case ExamStatusDisplay.APPROVED:
                return <IconCheckmarkCircle />;
            case ExamStatusDisplay.PENDINGRESELECT:
            case ExamStatusDisplay.NOTAPPROVED:
            case ExamStatusDisplay.ONHOLD:
                return <IconCautionTriangle />;
            case ExamStatusDisplay.PENDING:
            case ExamStatusDisplay.PENDINGAPPROVAL:
            case ExamStatusDisplay.PENDINGPROVIDERSCHEDULING:
            default:
                return <IconClock />;
        }
    }, []);

    const getStyleColor = useCallback((examStatus) => {
        switch (examStatus) {
            case ExamStatusDisplay.APPROVED:
            case ExamStatusDisplay.PENDING:
                return courseStyles.statusPassed;
            default:
                return courseStyles.statusFailed;
        }
    }, []);

    const styleColor = useMemo(() => (getStyleColor(status.ExamStatus)), [status.ExamStatus, getStyleColor]);

    return useMemo(
        () => (
            <span className={`${courseStyles.status} ${styles.statusMessage} ${styleColor}`}>
                <IconToUse examStatus={status.ExamStatus} />
                {status.ExamStatusDisplay}
            </span>
        ),
        [status.ExamStatus, status.ExamStatusDisplay, styleColor]
    );

}

function ExamHistoryContainer ({ examHistory }) {
    const MapExamHistoryRecords = useCallback(({ examHistory }) => {
        return <>{examHistory.map(exam => (<ExamHistoryRecord exam={exam} />))}</>;
    }, []);

    return useMemo(() => {
        if (!examHistory) {
            return false;
        }

        return (
            <div className={styles.examHistoryWrap}>
                <div className={styles.examHistoryHeader}>Exam History</div>
                <div className={styles.examHistory}>
                    <MapExamHistoryRecords examHistory={examHistory} />
                </div>
            </div>
        )
    }, [examHistory]);
}

function ExamHistoryRecord ({ exam }) {
    const ExamScore = useCallback(({ exam }) => {
        if (exam.Result !== ResultStatus.DNA) {
            return <span className={styles.examScore}>Score: {exam.Score} of {exam.MaxScore}</span>;
        }
        return false;
    }, []
    );

    const ExamHistoryStatusIcon = useCallback(({ result }) => {
        switch (result) {
            case ResultStatus.FAIL:
                return <IconSlashCircle fill="#E29907" />;
            case ResultStatus.DNA:
                return <IconDashCircle fill="#87A0AF" />;
            case ResultStatus.PASS:
            default:
                return <IconCheckmarkCircle fill="#4ABFAD" />;
        }
    }, []);

    const getExamHistoryStatusColor = useCallback((result) => {
        switch (result) {
            case ResultStatus.FAIL:
                return "statusFailed";
            case ResultStatus.DNA:
                return "statusDNA";
            case ResultStatus.PASS:
            default:
                return "statusPassed";
        }
    }, []);

    const examHistoryStatusColor = useMemo(() => (getExamHistoryStatusColor(exam.Result)), [exam.Result, getExamHistoryStatusColor]);

    return useMemo(() => {
        const link = exam.ActionLink;

        return (
            <div className={styles.examHistoryItem}>
                <div className={styles.examHistoryItemRow}>
                    <div className={`${styles.title}`}>Attempt {exam.AttemptNumber}</div>
                    <span className={`${styles.examHistoryStatus} ${courseStyles[examHistoryStatusColor]}`}>
                        <ExamHistoryStatusIcon result={exam.Result} /> {exam.ResultDisplay}
                    </span>
                </div>

                <div className={`${styles.examHistoryItemRow} ${styles.examHistoryItemRowScore}`}>
                    <div className={`${styles.examHistoryDate}`}>{exam.AttemptDate}</div>
                    <div className={`${styles.examHistoryScore}`}><ExamScore exam={exam} /></div>
                </div>

                <div className={styles.examHistoryItemRow}>
                    <CardLink
                        linkText={link.CardLinkText}
                        linkUrl={link.CardLinkUri}
                        linkIcon={<IconAngleRight />}
                        target={"_blank"}
                        className={buttonStyles.floating} />
                </div>
            </div>
        )
    }, [exam, examHistoryStatusColor]);
}

/* BEGIN -- Enums -- BEGIN */
export const CardViewType = {
    NO_VOUCHER: "NoVoucher",
    VOUCHER: "Voucher",
    EXAM_WITH_SCHEDULED_EVENT: "ExamWithScheduledEvent",
    EXAM_RESULTS: "ExamResults"
};

const ExamStatusDisplay = {
    PENDING: "Pending",
    PASS: "Pass",
    FAIL: "Fail",
    DNA: "DNA",
    PENDINGAPPROVAL: "PendingApproval",
    APPROVED: "Approved",
    NOTAPPROVED: "NotApproved",
    CANCELLED: "Cancelled",
    VOID: "Void",
    PENDINGREFUND: "PendingRefund",
    REFUNDED: "Refunded",
    PENDINGRESELECT: "PendingReselect",
    RESELECTED: "Reselected",
    RESCHEDULED: "Rescheduled",
    ONHOLD: "OnHold",
    PENDINGPROVIDERSCHEDULING: "PendingProviderScheduling"
}

const ResultStatus = {
    PENDING: "Pending",
    PASS: "Pass",
    FAIL: "Fail",
    DNA: "DNA"
}
/* END -- Enums -- END */

export default CertificationExamCard;
